import PropTypes from "prop-types"

import StatCard from "../stat_card"
import LineComparisonChart from "../charts/line_comparison_chart"
import SimpleLineChart from "../simple_line_chart"
import PeriodSelectionHOC from "../period_selection_hoc"
import engagementMetricsFetcher from "./engagement_metrics_fetcher"
import {
  calculateChangeRate,
  reduceSeriesToValue,
  computeChangeThisPeriod,
} from "../lib"

import "../stats.scss"

const daysInMonth = 30

const EngagementComponent = ({
  data: {
    serviceEngagements,
    serviceEngagementsLastPeriod,
    userEngagementRate,
    dailyMetrics,
  },
  timePeriodInDays,
  lineColorA,
  lineColorB,
  includeCTA,
  serviceName,
}) => (
  <>
    <div styleName="stats-container engagement">
      <div styleName="stats-box tall">
        <StatCard
          statText={"Service engagements"}
          stat={serviceEngagements}
          changeRate={calculateChangeRate(
            serviceEngagements,
            serviceEngagementsLastPeriod
          )}
          className="bigger"
          compactStat={false}
          timePeriodInDays={timePeriodInDays}
        />
        <StatCard
          statText={"User engagement rate"}
          stat={
            timePeriodInDays > daysInMonth
              ? userEngagementRate / Math.ceil(timePeriodInDays / daysInMonth)
              : userEngagementRate
          }
          subText={`Average times your users engaged with your service over the last ${timePeriodInDays} days`}
          statUnitText={`${serviceEngagements == 1 ? "Time" : "Times"}${
            timePeriodInDays > daysInMonth ? " per month" : ""
          }`}
          className="bigger"
          compactStat={false}
          timePeriodInDays={timePeriodInDays}
          tooltipText={`Number of service engagements for ${serviceName} per user in the last ${timePeriodInDays} days`}
          lowThreshold={0.5}
          lowValue={"<1"}
        />
      </div>
      <div styleName="chart">
        <LineComparisonChart
          data={dailyMetrics}
          metric={"service_engagement_daily"}
          height={350}
          timePeriodInDays={timePeriodInDays}
          units={"engagements"}
          lineOnly
        />
      </div>
      <div className="chart-legend horizontal">
        <div>
          <div className="line-color" style={{ backgroundColor: lineColorA }} />
          Last {timePeriodInDays} days
        </div>
        <div>
          <div className="line-color" style={{ backgroundColor: lineColorB }} />
          Prior {timePeriodInDays} days
        </div>
      </div>
    </div>
    <div styleName="grouped-charts">
      <SimpleLineChart
        chartTitle={`Applet runs`}
        data={dailyMetrics}
        currentValue={reduceSeriesToValue(
          dailyMetrics,
          timePeriodInDays,
          "applet_runs_daily"
        )}
        changeRate={computeChangeThisPeriod(
          dailyMetrics,
          timePeriodInDays,
          "applet_runs_daily"
        )}
        metric={"applet_runs_daily"}
        units={"runs"}
        timePeriodInDays={timePeriodInDays}
        tooltipText={`Number of successful Applet runs belonging to ${serviceName} in the last ${timePeriodInDays} days`}
        wide={!includeCTA}
      />
      <SimpleLineChart
        chartTitle={`Applet enables`}
        data={dailyMetrics}
        currentValue={reduceSeriesToValue(
          dailyMetrics,
          timePeriodInDays,
          "applet_enables_daily"
        )}
        changeRate={computeChangeThisPeriod(
          dailyMetrics,
          timePeriodInDays,
          "applet_enables_daily"
        )}
        metric={"applet_enables_daily"}
        units={"enables"}
        timePeriodInDays={timePeriodInDays}
        tooltipText={`Number of Applets enabled for ${serviceName} in the last ${timePeriodInDays} days`}
        wide={!includeCTA}
      />
      <SimpleLineChart
        chartTitle={`Applet ratings`}
        data={dailyMetrics}
        currentValue={reduceSeriesToValue(
          dailyMetrics,
          timePeriodInDays,
          "applet_ratings_daily"
        )}
        changeRate={computeChangeThisPeriod(
          dailyMetrics,
          timePeriodInDays,
          "applet_ratings_daily"
        )}
        metric={"applet_ratings_daily"}
        units={"ratings"}
        timePeriodInDays={timePeriodInDays}
        tooltipText={`Number of Applets rated for ${serviceName} in the last ${timePeriodInDays} days`}
        wide={!includeCTA}
      />
      {includeCTA && (
        <SimpleLineChart
          chartTitle="Call-to-action button clicks"
          data={dailyMetrics}
          currentValue={reduceSeriesToValue(
            dailyMetrics,
            timePeriodInDays,
            "cta_clicks_daily"
          )}
          changeRate={computeChangeThisPeriod(
            dailyMetrics,
            timePeriodInDays,
            "cta_clicks_daily"
          )}
          metric={"cta_clicks_daily"}
          units={"clicks"}
          timePeriodInDays={timePeriodInDays}
          tooltipText={`Number of clicks on the call-to-action button for ${serviceName} in the last ${timePeriodInDays} days`}
        />
      )}
    </div>
  </>
)

EngagementComponent.propTypes = {
  data: PropTypes.shape({
    dailyMetrics: PropTypes.arrayOf(PropTypes.object),
    serviceEngagements: PropTypes.number,
    serviceEngagementsLastPeriod: PropTypes.number,
    userEngagementRate: PropTypes.number,
  }),
  lineColorA: PropTypes.string,
  lineColorB: PropTypes.string,
  timePeriodInDays: PropTypes.number,
  serviceName: PropTypes.string,
}

EngagementComponent.defaultProps = {
  lineColorA: LineComparisonChart.defaultProps.lineColorA,
  lineColorB: LineComparisonChart.defaultProps.lineColorB,
  timePeriodInDays: LineComparisonChart.defaultProps.timePeriodInDays,
}

export default PeriodSelectionHOC({
  WrappedComponent: EngagementComponent,
  serviceMetricsFetcher: engagementMetricsFetcher,
  headerText: "Engagements",
})
